import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  cognitoConfigured,
  CognitoError,
  confirmEmailSignUp,
  initiateEmailOtp,
  refreshCognitoTokens,
  resendEmailSignUp,
  respondToEmailOtp,
  signUpWithEmail,
  type CognitoTokens,
} from "../features/account/cognito";
import {
  beginEmailAccess,
  confirmSignInEmailCode,
  ownerPreviewEnabled,
  ownerPreviewSignInOnlyError,
  type EmailCodeConfirmationResult,
  type EmailAccessMode,
  type EmailAccessOutcome,
} from "../features/account/email-access";
import { clearEmailCodeFragmentCache, safeReturnPath } from "../features/account/magic-sign-in";
import { clearRedemptionDraft } from "../features/commerce/redemption-draft";
import { clearGuidedStoryDrafts } from "../features/guided/guided-story-draft-keys";
import { clearStudioCommandJournal } from "../features/studio/studio-command-replay";
import {
  observeAppSession,
  observeSignOut,
  setClientErrorReportingToken,
} from "./customer-activity";
import { setSessionRenewal } from "./api";

type PendingChallenge = {
  email: string;
  kind: "SIGN_IN" | "SIGN_UP";
  session?: string;
  returnTo: string;
};

type AuthValue = {
  configured: boolean;
  tokens: CognitoTokens | null;
  accountEmail: string | null;
  restoringSession: boolean;
  sessionIssue: "expired" | "invalid" | null;
  pending: PendingChallenge | null;
  startEmail: (
    email: string,
    mode: EmailAccessMode,
    returnTo?: string,
  ) => Promise<{ tokens: CognitoTokens | null; outcome: EmailAccessOutcome }>;
  resendPendingCode: () => Promise<CognitoTokens | null>;
  confirmCode: (code: string) => Promise<EmailCodeConfirmationResult>;
  cancelChallenge: () => void;
  signOut: () => void;
  /**
   * Renews the sign-in now (one renewal at a time). Resolves to the fresh tokens, or null when
   * the session can't be renewed and has ended; rejects on a passing failure.
   */
  renewSession: () => Promise<CognitoTokens | null>;
};

/** Cognito refusing the sign-in itself: renewing again can never succeed. */
function definitiveRenewalRefusal(error: unknown) {
  return (
    error instanceof CognitoError &&
    [
      "NotAuthorizedException",
      "UserNotFoundException",
      "UserNotConfirmedException",
      "PasswordResetRequiredException",
    ].includes(error.code)
  );
}

/** A renewal failure that may pass: no network yet, throttling or a provider blip. */
function passingRenewalFailure(error: unknown) {
  if (error instanceof CognitoError)
    return [
      "TooManyRequestsException",
      "InternalErrorException",
      "LimitExceededException",
      "COGNITO_ERROR",
    ].includes(error.code);
  return (
    error instanceof TypeError || (typeof navigator !== "undefined" && navigator.onLine === false)
  );
}

const storageKey = "lifescapsule:customer-session";
const challengeStorageKey = "lifescapsule:customer-challenge";
const challengeLifetimeMs = 15 * 60 * 1_000;
const observedSessionKey = "lifescapsule:observed-customer-session";
/** Left by an explicit sign-out and removed by the next sign-in; an expiry never leaves it. */
const signedOutKey = "lifescapsule:customer-signed-out";

type StoredSession = {
  tokens: CognitoTokens | null;
  state: "active" | "restoring" | "signed-out";
  issue: "expired" | "invalid" | null;
};

function challengeStorage() {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** This tab's own storage: what belongs to one tab, and where sign-ins were kept before. */
function tabStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * A sign-in is kept for the whole browser, not one tab (owner decision, 2026-11-02, accepting
 * the shared-device trade-off): a new tab, or a link from an email, opens signed in. Where the
 * browser refuses local storage, the sign-in stays in this tab, as it did before.
 */
function sessionStorageArea(): Storage | undefined {
  try {
    if (window.localStorage) return window.localStorage;
  } catch {
    // Blocked local storage: keep the sign-in for this tab instead.
  }
  return tabStorage();
}

/**
 * The redesigned pages keep a checked voucher, a chosen book, an unsent help request and unsent
 * replies to help requests here.
 */
const voucherTabKeys = [
  "lc-w1-voucher-bookshop",
  "lc-w1-start-plan",
  "lc-support-draft-v1",
  "lc-support-replies-v1",
];

/** My Library keeps a reader's place, search and text choices per book in this tab. */
const readingToolsPrefix = "lc-book-tools-v1:";

function clearStoredSession() {
  for (const area of new Set([sessionStorageArea(), tabStorage()])) {
    try {
      area?.removeItem(storageKey);
    } catch {
      // A blocked storage API already means there is no reusable session to preserve.
    }
  }
  try {
    // Another account on this device mustn't see what the last one searched for in a shared book.
    for (const key of Object.keys(sessionStorage))
      if (key.startsWith(readingToolsPrefix)) sessionStorage.removeItem(key);
  } catch {
    // A blocked storage API already means there is nothing left in this tab to clear.
  }
}

/** Whether the shared sign-in was last ended by someone signing out, rather than expiring. */
function signedOutOnPurpose(): boolean {
  try {
    return (sessionStorageArea()?.getItem(signedOutKey) ?? null) !== null;
  } catch {
    return false;
  }
}

function markSignedOut(signedOut: boolean) {
  try {
    if (signedOut) sessionStorageArea()?.setItem(signedOutKey, String(Date.now()));
    else sessionStorageArea()?.removeItem(signedOutKey);
  } catch {
    // Without storage there are no other tabs' copies to keep signed out.
  }
}

function readSharedSession(): string | null {
  try {
    return sessionStorageArea()?.getItem(storageKey) ?? null;
  } catch {
    return null;
  }
}

function writeStoredSession(tokens: CognitoTokens) {
  const value = JSON.stringify(tokens);
  try {
    sessionStorageArea()?.setItem(storageKey, value);
  } catch {
    // A full or refusing local store still keeps the sign-in for this tab.
    try {
      tabStorage()?.setItem(storageKey, value);
    } catch {
      // Nothing can be kept: the sign-in lasts as long as this page.
    }
  }
}

function parseStoredSession(raw: string | null): StoredSession {
  try {
    if (!raw) return { tokens: null, state: "signed-out", issue: null };
    const value = JSON.parse(raw) as Partial<CognitoTokens> | null;
    if (
      !value ||
      typeof value.idToken !== "string" ||
      !value.idToken ||
      typeof value.accessToken !== "string" ||
      !value.accessToken ||
      typeof value.expiresAt !== "number" ||
      !Number.isFinite(value.expiresAt) ||
      (value.refreshToken !== undefined && typeof value.refreshToken !== "string")
    ) {
      return { tokens: null, state: "signed-out", issue: "invalid" };
    }
    const tokens = value as CognitoTokens;
    if (tokens.expiresAt <= Date.now() + 30_000) {
      return tokens.refreshToken
        ? { tokens, state: "restoring", issue: null }
        : { tokens: null, state: "signed-out", issue: "expired" };
    }
    return { tokens, state: "active", issue: null };
  } catch {
    return { tokens: null, state: "signed-out", issue: "invalid" };
  }
}

function storedSession(): StoredSession {
  const area = sessionStorageArea();
  let raw: string | null;
  try {
    raw = area?.getItem(storageKey) ?? null;
  } catch {
    return { tokens: null, state: "signed-out", issue: "invalid" };
  }
  // A sign-in kept by this tab before sign-ins were shared moves to the shared store once: the
  // shared one wins unless it can't be used, and the tab's copy is then removed. A sign-out in
  // any tab since then means the copy is dropped, never brought back.
  const tab = tabStorage();
  if (tab && tab !== area) {
    try {
      const legacy = tab.getItem(storageKey);
      if (legacy !== null) {
        const shared = parseStoredSession(raw);
        const kept = parseStoredSession(legacy);
        if (!signedOutOnPurpose() && (raw === null || (kept.tokens && !shared.tokens))) {
          raw = legacy;
          try {
            area?.setItem(storageKey, legacy);
            tab.removeItem(storageKey);
          } catch {
            // The shared store refused it: this tab keeps its own copy, as before.
          }
        } else {
          tab.removeItem(storageKey);
          // The tab's voucher progress and drafts belonged to the sign-in it just lost.
          if (kept.tokens && (!shared.tokens || !sameAccount(kept.tokens, shared.tokens)))
            forgetTabAccountState();
        }
      }
    } catch {
      // An unreadable tab store has nothing to move.
    }
  }
  return parseStoredSession(raw);
}

function emailFromIdToken(idToken: string | undefined) {
  if (!idToken) return null;
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const parsed = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as {
      email?: unknown;
    };
    return typeof parsed.email === "string" && parsed.email.includes("@")
      ? parsed.email.trim().toLowerCase()
      : null;
  } catch {
    return null;
  }
}

function sessionIdentityFromIdToken(idToken: string): string | null {
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const parsed = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as {
      sub?: unknown;
      auth_time?: unknown;
      iat?: unknown;
    };
    const issued = parsed.auth_time ?? parsed.iat;
    return typeof parsed.sub === "string" && typeof issued === "number"
      ? `${parsed.sub}:${issued}`
      : null;
  } catch {
    return null;
  }
}

function subjectFromIdToken(idToken: string | undefined): string | null {
  const identity = idToken ? sessionIdentityFromIdToken(idToken) : null;
  return identity ? identity.slice(0, identity.lastIndexOf(":")) : null;
}

/** Whether two sign-ins are the same account (tokens without a readable subject count as one). */
function sameAccount(a: CognitoTokens, b: CognitoTokens) {
  return subjectFromIdToken(a.idToken) === subjectFromIdToken(b.idToken);
}

/**
 * Tokens another tab saved for this same account after renewing it, which this tab can use
 * instead of renewing again (each tab renews one request at a time, but tabs don't wait on each
 * other).
 */
function newerSharedTokens(current: CognitoTokens) {
  let raw: string | null;
  try {
    raw = sessionStorageArea()?.getItem(storageKey) ?? null;
  } catch {
    return null;
  }
  const shared = parseStoredSession(raw);
  return shared.state === "active" &&
    shared.tokens &&
    shared.tokens.idToken !== current.idToken &&
    shared.tokens.expiresAt > current.expiresAt &&
    sameAccount(shared.tokens, current)
    ? shared.tokens
    : null;
}

/** What one tab keeps for the signed-in person, which goes when they sign out in any tab. */
function forgetTabAccountState() {
  clearEmailCodeFragmentCache();
  // Save-retry identities belong to this account; the next one must never reuse them.
  clearStudioCommandJournal();
  try {
    for (const key of Object.keys(sessionStorage))
      if (key.startsWith(readingToolsPrefix)) sessionStorage.removeItem(key);
    // A voucher checked in this tab, and a book chosen for it, go with the person signing out.
    for (const key of voucherTabKeys) sessionStorage.removeItem(key);
    sessionStorage.removeItem(observedSessionKey);
  } catch {
    // Session cleanup remains complete even if browser storage is blocked.
  }
}

/** Spreads tabs' timed renewals apart, so open tabs rarely renew the same sign-in together. */
function renewalSpreadMs() {
  return Math.floor(Math.random() * 15_000);
}

function storedChallenge(ownerPreview: boolean) {
  const storage = challengeStorage();
  if (!storage) return null;
  try {
    const value = JSON.parse(storage.getItem(challengeStorageKey) ?? "null") as
      | (PendingChallenge & { expiresAt?: number })
      | null;
    if (ownerPreview && value?.kind === "SIGN_UP") {
      storage.removeItem(challengeStorageKey);
      return null;
    }
    if (!value?.email || !["SIGN_IN", "SIGN_UP"].includes(value.kind)) return null;
    if (!value.expiresAt || value.expiresAt <= Date.now()) {
      storage.removeItem(challengeStorageKey);
      return null;
    }
    return {
      email: value.email,
      kind: value.kind,
      session: value.session,
      returnTo: safeReturnPath(value.returnTo),
    };
  } catch {
    storage.removeItem(challengeStorageKey);
    return null;
  }
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthSessionProvider({
  children,
  ownerPreview = ownerPreviewEnabled,
}: {
  children: React.ReactNode;
  ownerPreview?: boolean;
}) {
  const [initialSession] = useState(storedSession);
  const [sessionTokens, setSessionTokens] = useState<CognitoTokens | null>(initialSession.tokens);
  const [sessionState, setSessionState] = useState(initialSession.state);
  const [sessionIssue, setSessionIssue] = useState<"expired" | "invalid" | null>(
    initialSession.issue,
  );
  const [accountEmail, setAccountEmail] = useState<string | null>(() =>
    emailFromIdToken(initialSession.tokens?.idToken),
  );
  const [pending, setPending] = useState<PendingChallenge | null>(() =>
    storedChallenge(ownerPreview),
  );
  const tokens = sessionState === "active" ? sessionTokens : null;
  const latestTokens = useRef(sessionTokens);
  latestTokens.current = sessionTokens;
  const renewal = useRef<Promise<CognitoTokens | null> | null>(null);

  function clearPendingChallenge() {
    challengeStorage()?.removeItem(challengeStorageKey);
    setPending(null);
  }

  function rememberChallenge(next: PendingChallenge) {
    if (ownerPreview && next.kind === "SIGN_UP") {
      clearPendingChallenge();
      throw ownerPreviewSignInOnlyError();
    }
    challengeStorage()?.setItem(
      challengeStorageKey,
      JSON.stringify({ ...next, expiresAt: Date.now() + challengeLifetimeMs }),
    );
    setPending(next);
  }

  function remember(next: CognitoTokens, verifiedEmail?: string) {
    clearEmailCodeFragmentCache();
    markSignedOut(false);
    writeStoredSession(next);
    challengeStorage()?.removeItem(challengeStorageKey);
    latestTokens.current = next;
    setSessionTokens(next);
    setSessionState("active");
    setSessionIssue(null);
    setAccountEmail(verifiedEmail?.trim().toLowerCase() || emailFromIdToken(next.idToken) || null);
    setPending(null);
    return next;
  }

  useEffect(() => {
    const syncChallenge = (event: StorageEvent) => {
      if (event.key === challengeStorageKey) setPending(storedChallenge(ownerPreview));
    };
    window.addEventListener("storage", syncChallenge);
    return () => window.removeEventListener("storage", syncChallenge);
  }, [ownerPreview]);

  useEffect(() => {
    if (!sessionIssue) return;
    clearStoredSession();
  }, [sessionIssue]);

  /**
   * This tab follows the shared sign-in. `ended` says the shared one is known to have gone (another
   * tab removed it), not merely that it couldn't be read. A sign-out takes this tab's drafts with
   * it; an expiry keeps them for the next sign-in.
   */
  const followShared = useCallback((raw: string | null, ended: boolean) => {
    const next = parseStoredSession(raw);
    const current = latestTokens.current;
    if (!next.tokens) {
      if (!current || !ended) return;
      const signedOut = signedOutOnPurpose();
      if (signedOut) forgetTabAccountState();
      latestTokens.current = null;
      setSessionTokens(null);
      setSessionState("signed-out");
      setSessionIssue(signedOut ? null : "expired");
      setAccountEmail(null);
      return;
    }
    if (current?.idToken === next.tokens.idToken) return;
    const sameSignIn = Boolean(current && sameAccount(current, next.tokens));
    // An older copy of this same sign-in never replaces a newer one.
    if (current && sameSignIn && next.tokens.expiresAt <= current.expiresAt) return;
    if (current && !sameSignIn) forgetTabAccountState();
    latestTokens.current = next.tokens;
    setSessionTokens(next.tokens);
    setSessionState(next.state === "signed-out" ? "active" : next.state);
    setSessionIssue(null);
    const email = emailFromIdToken(next.tokens.idToken);
    setAccountEmail((previous) => email ?? (sameSignIn ? previous : null));
  }, []);

  // Every tab shares one sign-in: signing in, renewing or signing out in one reaches the others.
  useEffect(() => {
    const syncSession = (event: StorageEvent) => {
      // A null key means the whole store was cleared.
      if (event.key !== storageKey && event.key !== null) return;
      followShared(event.key === null ? readSharedSession() : event.newValue, true);
    };
    // A tab restored from the back/forward cache, or brought back, missed what other tabs did
    // while it was away: it catches up before it renews anything (registered before renewal's).
    const catchUp = () => {
      if (document.visibilityState === "hidden") return;
      followShared(readSharedSession(), signedOutOnPurpose());
    };
    window.addEventListener("storage", syncSession);
    window.addEventListener("pageshow", catchUp);
    document.addEventListener("visibilitychange", catchUp);
    return () => {
      window.removeEventListener("storage", syncSession);
      window.removeEventListener("pageshow", catchUp);
      document.removeEventListener("visibilitychange", catchUp);
    };
  }, [followShared]);

  const adoptSharedTokens = useCallback((next: CognitoTokens) => {
    latestTokens.current = next;
    setSessionTokens(next);
    setSessionState("active");
    setSessionIssue(null);
    setAccountEmail((email) => email ?? emailFromIdToken(next.idToken));
    return next;
  }, []);

  const endExpiredSession = useCallback(() => {
    clearStoredSession();
    latestTokens.current = null;
    setSessionTokens(null);
    setSessionState("signed-out");
    setSessionIssue("expired");
    setAccountEmail(null);
  }, []);

  const renewSession = useCallback((): Promise<CognitoTokens | null> => {
    const current = latestTokens.current;
    if (!current) return Promise.resolve(null);
    // Another tab may have renewed this sign-in already: use what it saved.
    const shared = newerSharedTokens(current);
    if (shared) return Promise.resolve(adoptSharedTokens(shared));
    if (!current.refreshToken) {
      // A sign-in without a refresh token can't be renewed: once it expires it ends honestly
      // instead of sending a token the API refuses (2026-11-01 voucher incident).
      if (current.expiresAt > Date.now() + 30_000) return Promise.resolve(current);
      endExpiredSession();
      return Promise.resolve(null);
    }
    if (renewal.current) return renewal.current;
    const refreshToken = current.refreshToken;
    renewal.current = refreshCognitoTokens(refreshToken)
      .then(
        (next) => {
          // A renewal that finishes after sign-out or a different sign-in never revives it. One
          // finishing after another tab renewed the same sign-in answers with what that tab saved.
          const latest = latestTokens.current;
          if (latest?.refreshToken !== refreshToken)
            return latest && sameAccount(latest, current) && latest.expiresAt > Date.now() + 30_000
              ? latest
              : null;
          // Nor does one from a tab that missed a sign-out, or a different sign-in, elsewhere: it
          // would sign every tab back in. This tab follows the shared sign-in instead.
          const sharedRaw = readSharedSession();
          const shared = parseStoredSession(sharedRaw);
          if (
            (!shared.tokens && signedOutOnPurpose()) ||
            (shared.tokens && !sameAccount(shared.tokens, current))
          ) {
            followShared(sharedRaw, true);
            return null;
          }
          writeStoredSession(next);
          latestTokens.current = next;
          setSessionTokens(next);
          setSessionState("active");
          setSessionIssue(null);
          setAccountEmail((email) => email ?? emailFromIdToken(next.idToken));
          return next;
        },
        (error: unknown) => {
          // Two tabs can renew at once. When this one is refused because the other already
          // replaced the sign-in, the replacement is used instead of ending every tab's sign-in.
          const renewed = newerSharedTokens(current);
          if (renewed) return adoptSharedTokens(renewed);
          throw error;
        },
      )
      .finally(() => {
        renewal.current = null;
      });
    return renewal.current;
  }, [adoptSharedTokens, endExpiredSession, followShared]);

  // Every signed-in API call can renew an expired sign-in once (shared/api.ts), but only for the
  // account it was sent as: another tab may have signed someone else in meanwhile.
  useEffect(() => {
    setSessionRenewal(async (refusedIdToken) => {
      // Refused with an older token than this tab now holds (another tab renewed): use this one.
      const held = latestTokens.current;
      const renewed =
        held && held.idToken !== refusedIdToken && held.expiresAt > Date.now() + 30_000
          ? held
          : await renewSession();
      return renewed && subjectFromIdToken(renewed.idToken) === subjectFromIdToken(refusedIdToken)
        ? renewed.idToken
        : null;
    });
    return () => setSessionRenewal(undefined);
  }, [renewSession]);

  useEffect(() => {
    const current = sessionTokens;
    if (!current) return;
    let active = true;
    let timer = 0;
    let running = false;
    let failingSince = 0;
    const attempt = async () => {
      if (!active || running) return;
      running = true;
      try {
        const renewed = await renewSession();
        // A sign-in without a refresh token comes back unchanged until it is due to end, and a
        // timer can fire a moment early: look again just before it ends, never stop watching.
        if (active && renewed === current && !current.refreshToken) {
          window.clearTimeout(timer);
          timer = window.setTimeout(
            () => void attempt(),
            Math.max(1_000, current.expiresAt - Date.now() - 29_000),
          );
        }
      } catch (error) {
        // A short retry keeps a temporary network/provider failure from ending a long authoring
        // session, even once the token has expired: a laptop that wakes before its network does
        // must not throw away unsaved work. Cognito refusing the sign-in ends it truthfully, and
        // so does a renewal still failing after ten minutes while the browser says it's online.
        if (!active) return;
        failingSince ||= Date.now();
        const online = typeof navigator === "undefined" || navigator.onLine !== false;
        const stuck = online && Date.now() - failingSince > 10 * 60_000;
        const expired = current.expiresAt <= Date.now();
        if (
          definitiveRenewalRefusal(error) ||
          (expired && (!passingRenewalFailure(error) || stuck))
        ) {
          endExpiredSession();
          return;
        }
        window.clearTimeout(timer);
        timer = window.setTimeout(() => void attempt(), 15_000);
      } finally {
        running = false;
      }
    };
    // Background tabs throttle or freeze timers, so a tab left for an hour can wake past its
    // renewal with a token the API refuses. Renew as soon as it's back if expiry is near.
    const wake = () => {
      if (!active || document.visibilityState === "hidden") return;
      // This tab just caught up with another tab (a renewal or a sign-out): that is watched now.
      if (latestTokens.current !== current) return;
      if (current.expiresAt - Date.now() > 120_000) return;
      window.clearTimeout(timer);
      void attempt();
    };
    timer = window.setTimeout(
      () => void attempt(),
      Math.max(
        0,
        current.expiresAt -
          Date.now() -
          (current.refreshToken ? 60_000 + renewalSpreadMs() : 30_000),
      ),
    );
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    window.addEventListener("pageshow", wake);
    return () => {
      active = false;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
      window.removeEventListener("pageshow", wake);
    };
  }, [sessionTokens?.expiresAt, sessionTokens?.refreshToken, renewSession, endExpiredSession]);

  // A page that fails while signed in can say so; signed out, nothing is reported.
  useEffect(() => {
    setClientErrorReportingToken(tokens?.idToken);
  }, [tokens?.idToken]);

  useEffect(() => {
    if (!tokens?.idToken) return;
    const sessionIdentity = sessionIdentityFromIdToken(tokens.idToken);
    if (!sessionIdentity) return;
    // One observation per sign-in, however many tabs it opens in.
    const observed = sessionStorageArea();
    try {
      if (observed?.getItem(observedSessionKey) === sessionIdentity) return;
      // Reserve the fingerprint before the request so React Strict Mode and fast token refreshes
      // cannot race two observations for the same Cognito authentication session.
      observed?.setItem(observedSessionKey, sessionIdentity);
    } catch {
      // A blocked storage API may produce a duplicate observation, which the event ID projection
      // handles safely. It must not interfere with authentication.
    }
    void observeAppSession(tokens.idToken).then((accepted) => {
      if (accepted) return;
      try {
        if (observed?.getItem(observedSessionKey) === sessionIdentity)
          observed?.removeItem(observedSessionKey);
      } catch {
        // Observability is best effort and never changes the signed-in session.
      }
    });
  }, [tokens?.idToken]);

  async function startSignIn(email: string, returnTo: string) {
    const normalized = email.trim().toLowerCase();
    const result = await initiateEmailOtp(normalized);
    if (result.tokens) return remember(result.tokens, normalized);
    rememberChallenge({ email: normalized, kind: "SIGN_IN", session: result.session, returnTo });
    return null;
  }

  async function startSignUp(email: string, returnTo: string) {
    const normalized = email.trim().toLowerCase();
    const result = await signUpWithEmail(normalized);
    if (result.UserConfirmed) return startSignIn(normalized, returnTo);
    rememberChallenge({ email: normalized, kind: "SIGN_UP", session: result.Session, returnTo });
    return null;
  }

  async function resumeSignUp(email: string, returnTo: string) {
    const normalized = email.trim().toLowerCase();
    await resendEmailSignUp(normalized);
    rememberChallenge({ email: normalized, kind: "SIGN_UP", returnTo });
    return null;
  }

  async function startEmail(email: string, mode: EmailAccessMode, requestedReturnTo = "/app") {
    clearEmailCodeFragmentCache();
    setSessionIssue(null);
    const returnTo = safeReturnPath(requestedReturnTo);
    return beginEmailAccess(
      mode,
      {
        signIn: () => startSignIn(email, returnTo),
        signUp: () => startSignUp(email, returnTo),
        resumeSignUp: () => resumeSignUp(email, returnTo),
      },
      { ownerPreview },
    );
  }

  async function confirmCode(code: string) {
    if (!pending) throw new Error("Start email verification first.");
    if (ownerPreview && pending.kind === "SIGN_UP") {
      clearPendingChallenge();
      throw ownerPreviewSignInOnlyError();
    }
    if (pending.kind === "SIGN_IN") {
      if (!pending.session) throw new Error("The sign-in session expired. Request another code.");
      let result: EmailCodeConfirmationResult;
      try {
        result = await confirmSignInEmailCode(
          {
            authenticate: () => respondToEmailOtp(pending.email, pending.session!, code),
            requestAccountConfirmation: async () => {
              await resendEmailSignUp(pending.email);
            },
          },
          { ownerPreview },
        );
      } catch (error) {
        if (
          ownerPreview &&
          error instanceof CognitoError &&
          error.code === "OWNER_PREVIEW_SIGN_IN_ONLY"
        )
          clearPendingChallenge();
        throw error;
      }
      clearEmailCodeFragmentCache();
      if (result.tokens)
        return {
          tokens: remember(result.tokens, pending.email),
          outcome: "AUTHENTICATED",
        } as const;
      rememberChallenge({ ...pending, kind: "SIGN_UP", session: undefined });
      return result;
    }
    let result: Awaited<ReturnType<typeof confirmEmailSignUp>>;
    try {
      // Cognito's passwordless sign-up flow returns a session from SignUp that must be
      // submitted with ConfirmSignUp. That produces the proof-of-authentication session used
      // below for automatic first sign-in, without sending a second code.
      result = await confirmEmailSignUp(pending.email, code, pending.session);
    } catch (error) {
      // A confirmation can succeed even when Cognito rejects the follow-on session handoff.
      // Restart in normal email-OTP mode instead of trapping the person in a now-invalid
      // confirmation form.
      if (
        error instanceof CognitoError &&
        ["NotAuthorizedException", "InvalidParameterException"].includes(error.code)
      ) {
        const authenticated = await startSignIn(pending.email, pending.returnTo);
        clearEmailCodeFragmentCache();
        return authenticated
          ? { tokens: authenticated, outcome: "AUTHENTICATED" as const }
          : { tokens: null, outcome: "SIGN_IN_CODE" as const };
      }
      throw error;
    }
    if (result.tokens)
      return { tokens: remember(result.tokens, pending.email), outcome: "AUTHENTICATED" } as const;
    clearEmailCodeFragmentCache();
    rememberChallenge({ ...pending, kind: "SIGN_IN", session: result.session });
    return { tokens: null, outcome: "SIGN_IN_CODE" } as const;
  }

  async function resendPendingCode() {
    if (!pending) throw new Error("Start email verification first.");
    if (ownerPreview && pending.kind === "SIGN_UP") {
      clearPendingChallenge();
      throw ownerPreviewSignInOnlyError();
    }
    clearEmailCodeFragmentCache();
    if (pending.kind === "SIGN_UP") {
      await resendEmailSignUp(pending.email);
      rememberChallenge({ ...pending, session: undefined });
      return null;
    }
    const result = await initiateEmailOtp(pending.email);
    if (result.tokens) return remember(result.tokens, pending.email);
    rememberChallenge({ ...pending, session: result.session });
    return null;
  }

  function signOut() {
    if (tokens?.idToken) void observeSignOut(tokens.idToken);
    clearRedemptionDraft();
    clearGuidedStoryDrafts();
    forgetTabAccountState();
    try {
      sessionStorageArea()?.removeItem(observedSessionKey);
    } catch {
      // Session cleanup remains complete even if browser storage is blocked.
    }
    // Removing the shared sign-in signs every other tab out too. The mark goes first, so they
    // (and any tab away from the front) know it was a sign-out, not an expiry.
    markSignedOut(true);
    clearStoredSession();
    challengeStorage()?.removeItem(challengeStorageKey);
    latestTokens.current = null;
    setSessionTokens(null);
    setSessionState("signed-out");
    setSessionIssue(null);
    setAccountEmail(null);
    setPending(null);
  }

  const value: AuthValue = {
    configured: cognitoConfigured,
    tokens,
    accountEmail,
    restoringSession: sessionState === "restoring",
    sessionIssue,
    pending,
    startEmail,
    resendPendingCode,
    confirmCode,
    cancelChallenge: () => {
      clearEmailCodeFragmentCache();
      clearRedemptionDraft();
      challengeStorage()?.removeItem(challengeStorageKey);
      setPending(null);
    },
    signOut,
    renewSession,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthSession() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthSessionProvider is missing");
  return value;
}
